/**
 * The rollout panel's reads, in one hook (LOCKER-408).
 *
 * Both the panel and the page header need the same derived counts — the panel
 * for its stacked bar, the page for the edit dialog's "how many organizations
 * does flipping the default affect" copy. Deriving them twice is how the two
 * numbers end up disagreeing on the same screen.
 *
 * Calling this hook from both places issues no extra requests: RTK Query
 * dedupes on endpoint + serialized args, and both call sites pass the same
 * ones, so they share one cache entry and one in-flight request.
 */
import { useMemo } from "react"
import { Feature } from "shared/feature"
import { FeatureAssignment } from "shared/featureAssignment"
import { readDefaultActivated } from "shared/featureResolution"
import {
  useGetFeatureAssignmentsByFeatureIdQuery,
  useGetTenantsQuery,
} from "src/features/things/thingsSaApi"
import {
  buildFeatureRolloutRows,
  countFeatureRollout,
  indexAssignmentsByTenant,
} from "./featureRolloutModel"

/**
 * Read limits. Both lists are read whole because the panel's counts are of ALL
 * organizations — a paginated read would silently under-report the rollout,
 * which is the class of bug this ticket is about.
 */
export const TENANTS_READ_LIMIT = 5000
export const ASSIGNMENTS_READ_LIMIT = 5000

export const useFeatureRollout = (feature: Feature | undefined) => {
  const featureId = feature?.id

  const tenantsQuery = useGetTenantsQuery({
    params: { limit: TENANTS_READ_LIMIT },
  })
  const assignmentsQuery = useGetFeatureAssignmentsByFeatureIdQuery(
    { featureId: featureId || "", params: { limit: ASSIGNMENTS_READ_LIMIT } },
    { skip: !featureId },
  )

  const defaultActivated = readDefaultActivated(feature)

  const rows = useMemo(
    () =>
      buildFeatureRolloutRows(
        tenantsQuery.data?.things,
        indexAssignmentsByTenant(
          assignmentsQuery.data?.things as FeatureAssignment[] | undefined,
        ),
        defaultActivated,
      ),
    [tenantsQuery.data, assignmentsQuery.data, defaultActivated],
  )

  const counts = useMemo(() => countFeatureRollout(rows), [rows])

  return {
    rows,
    counts,
    defaultActivated,
    isLoading: tenantsQuery.isLoading || assignmentsQuery.isLoading,
    error: tenantsQuery.error || assignmentsQuery.error,
    tenantsQuery,
    assignmentsQuery,
  }
}
